import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    SavedWord,
    SavedWordResponse,
    CreateSavedWordRequest,
    UpdateSavedWordRequest,
    SavedWordFilters,
    PaginationParams,
} from '@/services/vocabularyService';

// === ENHANCED WORD SAVING ACTIONS ===
export const fetchSavedWordsRequest = createAction<{
    filters?: SavedWordFilters;
    pagination?: PaginationParams;
}>('savedWords/fetchSavedWordsRequest');
export const fetchSavedWordsSuccess = createAction<SavedWordResponse>('savedWords/fetchSavedWordsSuccess');
export const fetchSavedWordsFailure = createAction<string>('savedWords/fetchSavedWordsFailure');

export const saveWordRequest = createAction<CreateSavedWordRequest>('savedWords/saveWordRequest');
export const saveWordSuccess = createAction<SavedWord>('savedWords/saveWordSuccess');
export const saveWordFailure = createAction<string>('savedWords/saveWordFailure');

export const updateSavedWordRequest = createAction<UpdateSavedWordRequest>('savedWords/updateSavedWordRequest');
export const updateSavedWordSuccess = createAction<SavedWord>('savedWords/updateSavedWordSuccess');
export const updateSavedWordFailure = createAction<string>('savedWords/updateSavedWordFailure');

export const removeSavedWordRequest = createAction<string>('savedWords/removeSavedWordRequest');
export const removeSavedWordSuccess = createAction<string>('savedWords/removeSavedWordSuccess');
export const removeSavedWordFailure = createAction<string>('savedWords/removeSavedWordFailure');

export const bulkSaveWordsRequest = createAction<{ wordIds: string[]; groupId?: string }>(
    'savedWords/bulkSaveWordsRequest',
);
export const bulkSaveWordsSuccess = createAction<SavedWord[]>('savedWords/bulkSaveWordsSuccess');
export const bulkSaveWordsFailure = createAction<string>('savedWords/bulkSaveWordsFailure');

export const bulkRemoveSavedWordsRequest = createAction<string[]>('savedWords/bulkRemoveSavedWordsRequest');
export const bulkRemoveSavedWordsSuccess = createAction<string[]>('savedWords/bulkRemoveSavedWordsSuccess');
export const bulkRemoveSavedWordsFailure = createAction<string>('savedWords/bulkRemoveSavedWordsFailure');

// === STATE INTERFACE ===
export interface SavedWordsState {
    savedWords: SavedWord[];
    savedWordFilters: SavedWordFilters;

    // Loading States
    isLoadingSavedWords: boolean;
    isSavingWord: boolean;
    isUpdatingSavedWord: boolean;
    isRemovingSavedWord: boolean;
    isBulkSavingWords: boolean;
    isBulkRemovingSavedWords: boolean;

    // Error States
    savedWordError: string | null;
}

const initialState: SavedWordsState = {
    savedWords: [],
    savedWordFilters: {},
    isLoadingSavedWords: false,
    isSavingWord: false,
    isUpdatingSavedWord: false,
    isRemovingSavedWord: false,
    isBulkSavingWords: false,
    isBulkRemovingSavedWords: false,
    savedWordError: null,
};

const savedWordsSlice = createSlice({
    name: 'savedWords',
    initialState,
    reducers: {
        setSavedWordFilters: (state, action: PayloadAction<SavedWordFilters>) => {
            state.savedWordFilters = action.payload;
        },
        clearSavedWordFilters: (state) => {
            state.savedWordFilters = {};
        },
        clearSavedWordError: (state) => {
            state.savedWordError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH SAVED WORDS ===
            .addCase(fetchSavedWordsRequest, (state) => {
                state.isLoadingSavedWords = true;
                state.savedWordError = null;
            })
            .addCase(fetchSavedWordsSuccess, (state, action) => {
                state.isLoadingSavedWords = false;
                state.savedWords = action.payload.results;
            })
            .addCase(fetchSavedWordsFailure, (state, action) => {
                state.isLoadingSavedWords = false;
                state.savedWordError = action.payload;
            })

            // === SAVE WORD ===
            .addCase(saveWordRequest, (state) => {
                state.isSavingWord = true;
                state.savedWordError = null;
            })
            .addCase(saveWordSuccess, (state, action) => {
                state.isSavingWord = false;
                state.savedWords.unshift(action.payload);
            })
            .addCase(saveWordFailure, (state, action) => {
                state.isSavingWord = false;
                state.savedWordError = action.payload;
            })

            // === UPDATE SAVED WORD ===
            .addCase(updateSavedWordRequest, (state) => {
                state.isUpdatingSavedWord = true;
                state.savedWordError = null;
            })
            .addCase(updateSavedWordSuccess, (state, action) => {
                state.isUpdatingSavedWord = false;
                const index = state.savedWords.findIndex((savedWord) => savedWord.id === action.payload.id);
                if (index !== -1) {
                    state.savedWords[index] = action.payload;
                }
            })
            .addCase(updateSavedWordFailure, (state, action) => {
                state.isUpdatingSavedWord = false;
                state.savedWordError = action.payload;
            })

            // === REMOVE SAVED WORD ===
            .addCase(removeSavedWordRequest, (state) => {
                state.isRemovingSavedWord = true;
                state.savedWordError = null;
            })
            .addCase(removeSavedWordSuccess, (state, action) => {
                state.isRemovingSavedWord = false;
                state.savedWords = state.savedWords.filter((savedWord) => savedWord.id !== action.payload);
            })
            .addCase(removeSavedWordFailure, (state, action) => {
                state.isRemovingSavedWord = false;
                state.savedWordError = action.payload;
            })

            // === BULK SAVE WORDS ===
            .addCase(bulkSaveWordsRequest, (state) => {
                state.isBulkSavingWords = true;
                state.savedWordError = null;
            })
            .addCase(bulkSaveWordsSuccess, (state, action) => {
                state.isBulkSavingWords = false;
                state.savedWords.unshift(...action.payload);
            })
            .addCase(bulkSaveWordsFailure, (state, action) => {
                state.isBulkSavingWords = false;
                state.savedWordError = action.payload;
            })

            // === BULK REMOVE SAVED WORDS ===
            .addCase(bulkRemoveSavedWordsRequest, (state) => {
                state.isBulkRemovingSavedWords = true;
                state.savedWordError = null;
            })
            .addCase(bulkRemoveSavedWordsSuccess, (state, action) => {
                state.isBulkRemovingSavedWords = false;
                state.savedWords = state.savedWords.filter((savedWord) => !action.payload.includes(savedWord.id));
            })
            .addCase(bulkRemoveSavedWordsFailure, (state, action) => {
                state.isBulkRemovingSavedWords = false;
                state.savedWordError = action.payload;
            });
    },
});

export const { setSavedWordFilters, clearSavedWordFilters, clearSavedWordError } = savedWordsSlice.actions;

export default savedWordsSlice.reducer;
